import { Component, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, } from '@angular/router';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { EmbedBuilderComponent } from 'src/app/components/embed-builder/embed-builder.component';
import { DiscordEmbed, } from 'src/app/components/embed-builder/embed.model';
import { BotGuild, ChannelPreview, CustomEmoji, RepeatingEvent, SignupGroupTemplate, SignupTemplate } from 'src/app/models/bot/guild';
import { BotResourceService } from 'src/app/services/bot-resource.service';
import { EmbedService } from 'src/app/services/embed.service';
import { EmojiComponentService } from 'src/app/services/emojiComponent.service';
import { GuildService } from 'src/app/services/guild.service';

interface Event {
  title: string;
  description: string;
  text: string;
  start: Date;
  end: Date;
  footerText: string;
  footerIcon: string;
  embedImage: string;
  embedThumbnail: string;
  templateHash: string;
}

@Component({
  selector: 'app-repeating-event-card',
  templateUrl: './repeating-event-card.component.html',
  styleUrls: [
    '../../../shared/styles/action-buttons.css',
    '../shared/styles/guild-dashboard-shared.css',
    './repeating-event-card.component.css'
  ]
})
export class RepeatingEventCardComponent extends EmbedBuilderComponent implements OnInit {
  faIcons = {
    trash: faTrashAlt,
  };

  guildId: string;
  guild: BotGuild | undefined;
  signupTemplates: SignupTemplate[];
  channelSignupTemplateMapping: Map<string, SignupTemplate>;
  allowedChannels: ChannelPreview[];
  serverCustomEmojis: CustomEmoji[];
  eventChannel: string;
  eventForm: FormGroup;
  startPicker: any[];
  currEmbed: Partial<DiscordEmbed> = {};
  creationInProgress = false;
  signupGroups: SignupGroupTemplate[] = [];
  filteredSignupGroups: SignupGroupTemplate[] = [];
  members: any[] = [];
  repeatingEventId: string;
  repeatingEvent: RepeatingEvent;

  dateControl: FormControl;
  startMinDate: Date;
  endMinDate: Date;
  endMinDateIso: string;
  maxDate: Date;

  constructor(
    embedService: EmbedService,
    private route: ActivatedRoute,
    private guildService: GuildService,
    private botService: BotResourceService,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private emojiComponentService: EmojiComponentService,
    private router: Router,
  ) {
    super(embedService);
  }

  ngOnInit() {
    this.new_build_form();
    this.embedService.resetEmbed();

    this.startMinDate = new Date();
    this.endMinDate = new Date();  // TODO: Those need a rework. At least for the eventCreationTime picker
    this.endMinDateIso = this.endMinDate.toISOString();
    this.maxDate = new Date(
      this.startMinDate.getFullYear() + 1,
      this.startMinDate.getMonth(),
      this.startMinDate.getDate()
    );
    this.guildId = this.route.parent.snapshot.paramMap.get('id');
    this.route.paramMap.subscribe(params => {
      this.repeatingEventId = params.get('repeatingEventId');
      this.getRepeatingEvent(this.repeatingEventId).subscribe(repeatingEvent => {
        this.repeatingEvent = repeatingEvent;
        this.patchForm();
      });
    });

    const emojis$ = this.botService
      .getGuildEmoji(this.guildId)
      .pipe(shareReplay(1));
    const guildData$ = this.guildService.getGuildData(this.guildId);
    const eventCreateInfos$ = this.botService.getEventCreateInfos(this.guildId);
    const signupGroups$ = this.botService.getSignupGroups(this.guildId);
    const members$ = this.botService.getGuildMembers(this.guildId);

    forkJoin([emojis$, guildData$, eventCreateInfos$, signupGroups$, members$ ]).subscribe(
      ([emojis, guild, infos, signupGroups, members]) => {

        // Sort members
        this.members = members.sort((a, b) =>
          a.display_name.localeCompare(b.display_name)
        );

        // Process emojis
        emojis.forEach((emoji) => {
          emoji.shortNames = [emoji.name, emoji.full_id];
        });
        this.serverCustomEmojis = emojis;

        // Process guild data
        this.guild = guild;
        if (!this.guild.has_premium) {
          this.eventForm.get('embedImage').disable();
          this.eventForm.get('embedThumbnail').disable();
          this.eventForm.get('footerIcon').disable();
          this.eventForm.get('footerText').disable();
          this.eventForm.get('signupGroup').disable();
          this.eventForm.get('intervalType')?.disable();
          this.eventForm.get('intervalDays')?.disable();
          this.eventForm.get('dayOfMonth')?.disable();
          this.eventForm.get('ordinal')?.disable();
          this.eventForm.get('weekday')?.disable();
          this.eventForm.get('weekdays')?.disable();
        }
        this.eventForm.get('daysInAdvance').addValidators(Validators.max(7));

        const now = new Date();
        const timeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        this.eventForm.get('eventCreationTime')?.setValue(timeString);

        // Process event creation infos
        this.signupTemplates = infos.signup_templates;
        this.channelSignupTemplateMapping = infos.channel_template_mapping;
        this.allowedChannels = infos.channels_allowed;
        this.eventForm
          .get('eventChannel')
          .setValue(this.allowedChannels[0]?.id || '');
        this.eventForm
          .get('signupTemplate')
          .setValue(
            this.channelSignupTemplateMapping[
              this.eventForm.get('eventChannel').value
            ]
          );

        this.signupGroups = signupGroups;
        this.filteredSignupGroups = this.signupGroups.filter(
          (group) =>
            group.signupTemplate ===
            this.eventForm.get('signupTemplate')?.value
        );

        // Init form
        this.initForm();
      }
    );
  }

  new_build_form(){
    this.eventForm = this.fb.group({
      eventChannel: ['', [Validators.required, Validators.minLength(1)]],
      title: ['', [Validators.required, Validators.maxLength(60)]],
      description: ['', [Validators.maxLength(700)]],
      text: ['', [Validators.maxLength(2000)]],
      start: [this.formatDate(new Date()), Validators.required],
      end: [''],
      footerText: ['', [Validators.maxLength(1900)]],
      footerIcon: [''],
      embedImage: [''],
      embedThumbnail: [''],
      signupTemplate: ['', Validators.required],
      signupGroup: [''],
      daysInAdvance: [null, [Validators.min(0), Validators.pattern("^[0-9]*$"), Validators.required]],
      eventCreationTime: ['', Validators.required],
      intervalType: ['every7Days', Validators.required],
      intervalDays: [1],
      dayOfMonth: [1],
      ordinal: ['First'],
      weekday: ['Monday'],
      weekdays: [[]],
    });
  }

  patchForm() {
    this.eventForm.patchValue({
      eventChannel: this.allowedChannels?.find(c => c.id === this.repeatingEvent.eventChannel)?.id || '',
      title: this.repeatingEvent.title || '',
      description: this.repeatingEvent.description || '',
      text: this.repeatingEvent.text || '',
      start: this.repeatingEvent.start ? new Date(this.repeatingEvent.start) : this.formatDate(new Date()),
      end: this.repeatingEvent.end || '',
      footerText: this.repeatingEvent.footerText || '',
      footerIcon: this.repeatingEvent.footerIcon || '',
      embedImage: this.repeatingEvent.embedImage || '',
      embedThumbnail: this.repeatingEvent.embedThumbnail || '',
      signupTemplate: this.signupTemplates?.find(t => t.identifier === this.repeatingEvent.signupTemplate)?.identifier || '',
      signupGroup: this.signupGroups?.find(t => t.identifier == this.repeatingEvent.signupGroup)?.identifier || '',
      daysInAdvance: this.repeatingEvent.daysInAdvance === 0 ? 0 : this.repeatingEvent.daysInAdvance || null,
      eventCreationTime: this.repeatingEvent.eventCreationTime || '',
      intervalType: this.repeatingEvent.intervalType || 'every7Days',
      intervalDays: this.repeatingEvent.intervalDays || 1,
      dayOfMonth: this.repeatingEvent.dayOfMonth || 1,
      ordinal: ['First', 'Second', 'Third', 'Fourth'][Number(this.repeatingEvent.ordinal) - 1] || 'First',
      weekday: isNaN(Number(this.repeatingEvent.weekday))
        ? this.repeatingEvent.weekday || 'Monday'
        : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][Number(this.repeatingEvent.weekday) % 7],
      weekdays: this.repeatingEvent.weekdays
        ? this.repeatingEvent.weekdays.map((day) =>
            ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][Number(day)]
          )
        : [],
    });

    if (this.repeatingEventId) {
      this.eventForm.get('start')?.disable();
      this.eventForm.get('end')?.disable();
      this.eventForm.get('daysInAdvance')?.disable();
      this.eventForm.get('eventCreationTime')?.disable();
      this.eventForm.get('intervalType')?.disable();
      this.eventForm.get('intervalDays')?.disable();
      this.eventForm.get('dayOfMonth')?.disable();
      this.eventForm.get('ordinal')?.disable();
      this.eventForm.get('weekday')?.disable();
      this.eventForm.get('weekdays')?.disable();
    } else {
      this.eventForm.get('start')?.enable();
    }

    this.eventForm.markAllAsTouched();
  }

  getRepeatingEvent(repeatngEventId: string): Observable<RepeatingEvent> {
    if (!repeatngEventId) {
      return of({
        title: '',
        start: '',
        end: '',
        description: '',
        footerText: '',
        footerIcon: '',
        embedImage: '',
        embedThumbnail: '',
        signupTemplate: '',
        text: '',
        eventChannel: '',
        intervalType: 'every7Days',
        intervalDays: 1,
        dayOfMonth: 1,
        ordinal: 'First',
        weekday: 'Monday',
        weekdays: [],
      } as RepeatingEvent);
    }
    return this.botService.getRepeatingEvent(this.guildId, repeatngEventId);
  }


  initForm(): void {
    this.patchForm();

    this.eventForm.get('start').valueChanges.subscribe((startDate) => {
      this.endMinDate = new Date(startDate);
      if (
        this.eventForm.get('end').value &&
        this.eventForm.get('end').value < this.endMinDate
      ) {
        this.eventForm.get('end').setValue(this.formatDate(this.endMinDate));
      }
    });

    this.eventForm
      .get('signupTemplate')
      .valueChanges.subscribe((signupTemplateIdentifier) => {
        // signupTemplate changes update the event embed emojis
        const signupTemplate = this.getSignupTemplateFromEventForm();
        if (!signupTemplate) {
          this.toastr.error(
            'This repeating event uses an invalid signup template!',
            'Invalid Signup Template'
          );
          return;
        }
        this.filteredSignupGroups = this.signupGroups.filter(
          (group) => group.signupTemplate === signupTemplateIdentifier
        );
        this.eventForm.get('signupGroup').setValue('');

        this.signupTemplateEmojisToEmbedFields(signupTemplate);
      });

    this.filteredSignupGroups = this.signupGroups.filter(
      (group) => group.signupTemplate === this.repeatingEvent.signupTemplate
    );

    this.eventForm.valueChanges.subscribe(() => {
      // All changes update the event embed
      this.eventFormToEmbed();
      this.embedService.updateEmbed(this.currEmbed);
    });

    this.eventForm.get('signupGroup')?.valueChanges.subscribe(() => {
      const signupTemplate = this.getSignupTemplateFromEventForm();
      if (signupTemplate) {
        this.signupTemplateEmojisToEmbedFields(signupTemplate);
      }
    });

    // Do initial setup of embed
    this.eventFormToEmbed();
    this.signupTemplateEmojisToEmbedFields(
      this.getSignupTemplateFromEventForm()
    );
    this.embedService.updateEmbed(this.currEmbed);
  }

  getSignupTemplateFromEventForm() {
    return this.signupTemplates?.find(
      (st) => st.identifier === this.eventForm.get('signupTemplate').value
    );
  }

  eventFormToEmbed(): void {
    const startDateTime = this.formatDateTime(
      this.eventForm.get('start').value
    );
    const endTime = this.eventForm.get('end').value
      ? this.formatDateTime(this.eventForm.get('end').value).time
      : 'None';

    this.currEmbed.title = this.eventForm.get('title').value;
    this.currEmbed.description =
      '**Event Info:**\n' +
      '🗓️ ' +
      startDateTime.date +
      '\n' +
      '🕑 ' +
      startDateTime.time +
      ' - ' +
      endTime +
      '\n' +
      '**Description:**  \n' +
      this.eventForm.get('description').value;
    this.currEmbed.text = this.eventForm.get('text').value;
    this.currEmbed.footerText = this.eventForm.get('footerText').value;
    this.currEmbed.footerIconUrl = this.eventForm.get('footerIcon').value;
    this.currEmbed.url = this.eventForm.get('embedImage').value;
    this.currEmbed.thumbnailUrl = this.eventForm.get('embedThumbnail').value;
  }

  formatDateTime(datetime: string): { date: string; time: string } {
    const dateObj = new Date(datetime);
    const date = dateObj.toLocaleDateString();
    const time = dateObj.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
    return { date, time };
  }

  formatDate(date: Date): string {
    const d = new Date(date);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    const hours = ('0' + d.getHours()).slice(-2);
    const minutes = ('0' + d.getMinutes()).slice(-2);
    return `${d.getFullYear()}-${month}-${day}T${hours}:${minutes}`;
  }

  onSubmit(): void {
    if (!this.eventForm.valid) return;

    this.creationInProgress = true;

    if (this.eventForm.get('end').value === null) {
      // If the user adds an end date and than removes it again,
      // the end date is set to null for some reason. Backend needs an empty
      // string, so we set it back to empty.
      this.eventForm.get('end').setValue('');
    }

    const event: Event = this.eventForm.getRawValue();

    if (!this.repeatingEventId) {
      this.botService.createRepeatingEvent(this.guildId, event).subscribe({
        next: (response: any) => {
          if (response.status === 201){
            const identifier = response.body.id;
            const newTemplatePath = `/guilds/${this.guildId}/repeating-event/${identifier}`;
            this.guild.repeating_events.push({
              id: identifier,
              name: response.body.name,
              access: response.body.access
            });
            this.router.navigate([newTemplatePath]); // Navigate to the new URL
          }
        },
        error: (error: any) => {
          console.error('Error creating event:', error);
        },
      });
    } else {
      this.botService.updateRepeatingEvent(this.guildId, event, this.repeatingEventId).subscribe({
        next: (response: any) => {
          const identifier = response.body.id;
          const newTemplatePath = `/guilds/${this.guildId}/repeating-event/${identifier}`;

          // Find the index of the old/current repeating event entry
          const index = this.guild.repeating_events.findIndex(event => event.id === this.repeatingEventId);
          if (index !== -1) {
            // Replace the old entry with the new entry at the same index
            this.guild.repeating_events[index] = {
              id: identifier,
              name: response.body.name,
              access: response.body.access
            };
          } else {
            // If old entry is not found, just append the new entry
            this.guild.repeating_events.push({
              id: identifier,
              name: response.body.name,
              access: response.body.access
            });
          }

          this.router.navigate([newTemplatePath]);
        },
        error: (error: any) => {
          console.error('Error updating repeating event:', error);
        },
      });
    }
    this.creationInProgress = false;
  }

  generateControlName(name: string): string {
    // Normalize control name (e.g., "Tank, magic" -> "tank_magic")
    return name.toLowerCase().replace(/\W+/g, '_');
  }

  private convertEmojisObjectToArray(emojisObject: Record<string, any>): any[] {
    return Object.keys(emojisObject).map(mainEmojiKey => {
      const mainEmoji = emojisObject[mainEmojiKey];
      if (!mainEmoji.field_name) return [];
      const controlName = this.generateControlName(mainEmoji.field_name);

      const subEmojis = mainEmoji.emojis
        ? Object.keys(mainEmoji.emojis).map((subEmojiKey) => {
            const subEmoji = mainEmoji.emojis[subEmojiKey];
            return {
              emoji: subEmojiKey,
              fieldName: subEmoji.field_name,
              controlName: this.generateControlName(subEmoji.field_name),
            };
          })
        : [];

      return {
        emoji: mainEmojiKey,
        fieldName: mainEmoji.field_name,
        controlName,
        type: mainEmoji.type,
        subEmojis: subEmojis,
        limit: 0 // Default limit, adjust if needed
      };
    });
  }

  private signupTemplateEmojisToEmbedFields(signupTemplate): void {
    const selectedGroupId = this.eventForm.get('signupGroup')?.value;
    const signupGroup = this.signupGroups?.find(g => g.identifier === selectedGroupId);

    let selectedMembersMap: Record<string, any[]> = {};
    if (signupGroup?.userSelections) {
      selectedMembersMap = Object.entries(signupGroup.userSelections).reduce((acc, [key, userIds]) => {
        const parts = key.split(',');
        const fieldName = parts.length === 4 ? parts[3] : parts[2];
        const controlName = this.generateControlName(fieldName);

        acc[controlName] = userIds.map((uid) => {
          const member = this.members.find(
            (m) => m.user_id === uid || m.id === uid
          );
          return member
            ? { user_id: member.user_id, display_name: member.display_name }
            : { user_id: uid, display_name: 'Unknown' };
        });

        return acc;
      }, {});
    }

    if (signupTemplate) {
      this.emojiComponentService.signupTemplateEmojisToEmbedFields(
        // signupTemplate,
        this.embedService,
        this.serverCustomEmojis,
        { currEmbed: this.currEmbed },
        {
          emojis: this.convertEmojisObjectToArray(signupTemplate.emojis),
          emojiBehavior: signupTemplate.emoji_behavior,
          useButtons: signupTemplate.use_buttons,
          color: signupTemplate.color,
          emojiTitle: signupTemplate.emoji_title,
        },
        signupGroup?.userSelections ? selectedMembersMap : undefined,
        this.members
      );
    }
  }

  deleteRepeatingEntry() {
    this.botService.deleteRepeatingEvent(this.guildId, this.repeatingEvent.eventChannel, this.repeatingEventId).subscribe({
      next: () => {
        this.guild.repeating_events = this.guild.repeating_events.filter(event => event.id !== this.repeatingEventId);
        this.router.navigate([`/guilds/${this.guildId}/repeating-event/new`]);
      },
      error: (error: any) => {
        console.error('Error deleting template:', error);
        // Add error handling logic here, like showing an error message
      }
    });
  }
}
